// Utils
import globalLogger from '../../../logger/logger';
import { getLocationParentId } from '../../../../common/elements/utils/elementPropertyUtils';
import { isLocationTrash } from '../../../../common/elements/utils/elementLocationUtils';
import { getPhysicalAncestors } from '../../../../common/elements/utils/elementTraversalUtils';

// Types
import { IdGraph } from '../../../../common/dataStructures/graphTypes';
import { ImMNElementMap } from '../../../../common/elements/elementModelTypes';
import { PreferredAliasIdByBoardId } from '../breadcrumbTypes';

const logger = globalLogger.createChannel('breadcrumb-util');

/**
 * Keeps a preferred alias first without adding alias IDs that were saved for a board but no longer link to it.
 */
const orderAliasIds = (aliasIds: string[], preferredAliasId?: string): string[] => {
    if (!preferredAliasId || !aliasIds.includes(preferredAliasId)) return aliasIds;

    return [preferredAliasId, ...aliasIds.filter((aliasId) => aliasId !== preferredAliasId)];
};

/**
 * Determines whether an element's physical path contains a trashed element.
 */
const isElementOrAncestorInTrash = (elements: ImMNElementMap, elementId: string): boolean =>
    getPhysicalAncestors(elements, elementId).some(isLocationTrash);

/**
 * Determines whether an alias can represent a live breadcrumb path to its linked board.
 */
export const isValidAliasParent = (elements: ImMNElementMap, aliasId: string, linkedBoardId: string): boolean => {
    if (isElementOrAncestorInTrash(elements, aliasId)) return false;
    if (isElementOrAncestorInTrash(elements, linkedBoardId)) return false;

    return true;
};

/**
 * Finds adjacent node IDs: the physical parent ID and alias IDs.
 */
export const findParentIds = (
    elements: ImMNElementMap,
    boardIdToAliasIdsMap: IdGraph,
    elementId: string,
    preferredAliasIdByBoardId?: PreferredAliasIdByBoardId,
): string[] => {
    const element = elements.get(elementId);

    if (!element) {
        logger.info('No element in BFS', elementId);
        return [];
    }

    const aliasIds = boardIdToAliasIdsMap[elementId] || [];
    const validAliasIds = aliasIds.filter((aliasId) => isValidAliasParent(elements, aliasId, elementId));
    const parentId = getLocationParentId(element);
    const preferredAliasId = preferredAliasIdByBoardId?.[elementId];
    const orderedAliasIds = orderAliasIds(validAliasIds, preferredAliasId);

    return [parentId, ...orderedAliasIds].filter((elementId) => Boolean(elementId));
};
